/**
 * EmptyState Component
 *
 * Estado vacío reutilizable: un bloque centrado con ilustración, título,
 * descripción y opciones de acción primaria/secundaria. Se usa en páginas
 * donde todavía no hay datos (dashboard, conversaciones, calendar, etc.)
 * para guiar al usuario hacia la primera acción.
 *
 * Diseño inspirado en hosts de PMS modernos, con CTA
 * verde brand y soporte para sidebar/sección dentro de cards.
 */

import React, { forwardRef, type ReactNode } from 'react';
import { tokens } from '../tokens';
import { Button, type ButtonVariant } from './Button';

export interface EmptyStateAction {
  /** Texto del botón */
  label: string;
  /** Callback al clic. Si se pasa `href`, se ignora. */
  onClick?: () => void;
  /** Si se pasa, el botón renderiza como anchor (<a>) */
  href?: string;
  /** Si href está, define el target del anchor */
  target?: '_blank' | '_self';
  /** Variante visual del botón (por defecto: cta para primary, ghost para secondary) */
  variant?: ButtonVariant;
  /** Ícono opcional al inicio del label */
  icon?: ReactNode;
}

export interface EmptyStateProps {
  /** Ilustración o ícono central (SVG idealmente, ~96px) */
  illustration?: ReactNode;
  /** Título principal del estado vacío */
  title: string;
  /** Descripción que aparece debajo del título */
  description?: ReactNode;
  /** Acción principal (CTA verde por defecto) */
  primaryAction?: EmptyStateAction;
  /** Acción secundaria (link/ghost por defecto) */
  secondaryAction?: EmptyStateAction;
  /** Estilo del contenedor */
  variant?: 'card' | 'plain';
  /** Estilos adicionales para el contenedor */
  style?: React.CSSProperties;
  /** Compactar el espaciado (útil para sidebars/popovers) */
  compact?: boolean;
}

/** Ilustración por defecto: nube con destellos, en violeta soft. */
function DefaultIllustration() {
  return (
    <svg width="120" height="96" viewBox="0 0 120 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <linearGradient id="va-empty-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tokens.colors.accent[100]} />
          <stop offset="100%" stopColor={tokens.colors.accent[500]} stopOpacity="0.45" />
        </linearGradient>
      </defs>
      <ellipse cx="60" cy="84" rx="42" ry="6" fill={tokens.colors.neutral[100]} />
      <path
        d="M34 64a14 14 0 0 1 4-27.4 19 19 0 0 1 36.4-3.6A14 14 0 1 1 86 64H34z"
        fill="url(#va-empty-grad)"
        stroke={tokens.colors.accent[500]}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="92" cy="24" r="3" fill={tokens.colors.brand[500]} opacity={0.85} />
      <circle cx="22" cy="28" r="2" fill={tokens.colors.accent[600]} opacity={0.7} />
      <circle cx="100" cy="50" r="2" fill={tokens.colors.accent[400] ?? tokens.colors.accent[500]} opacity={0.6} />
    </svg>
  );
}

function renderAction(action: EmptyStateAction, fallbackVariant: ButtonVariant) {
  const variant = action.variant ?? fallbackVariant;
  const button = (
    <Button variant={variant} leftIcon={action.icon} onClick={action.onClick}>
      {action.label}
    </Button>
  );

  if (!action.href) return button;

  return (
    <a
      href={action.href}
      target={action.target ?? '_self'}
      rel={action.target === '_blank' ? 'noreferrer' : undefined}
      style={{ textDecoration: 'none' }}
    >
      {button}
    </a>
  );
}

/**
 * Estado vacío con título, descripción y hasta dos acciones.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="Conectá tu primer canal"
 *   description="Sumá WhatsApp, Instagram o Messenger para empezar a recibir conversaciones."
 *   primaryAction={{
 *     label: 'Agregar canal',
 *     onClick: () => navigate('/app/integrations'),
 *   }}
 *   secondaryAction={{
 *     label: 'Ver guía',
 *     href: 'https://docs.virtual-agents.app/consola/canales',
 *     target: '_blank',
 *   }}
 * />
 * ```
 */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  {
    illustration,
    title,
    description,
    primaryAction,
    secondaryAction,
    variant = 'card',
    style,
    compact = false,
  },
  ref
) {
  const padding = compact ? tokens.spacing[6] : tokens.spacing[10];

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding,
    gap: tokens.spacing[4],
    background: variant === 'card' ? tokens.semantic.surface : 'transparent',
    border: variant === 'card' ? `1px solid ${tokens.semantic.border}` : 'none',
    borderRadius: variant === 'card' ? tokens.borderRadius.xl : 0,
    boxShadow: variant === 'card' ? '0 1px 3px rgba(15, 23, 42, 0.05)' : 'none',
    ...style,
  };

  const titleStyles: React.CSSProperties = {
    margin: 0,
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: compact ? tokens.typography.fontSize.lg : tokens.typography.fontSize['2xl'],
    fontWeight: tokens.typography.fontWeight.bold,
    color: tokens.semantic.textPrimary,
    letterSpacing: '-0.02em',
    lineHeight: tokens.typography.lineHeight.tight,
  };

  const descriptionStyles: React.CSSProperties = {
    margin: 0,
    maxWidth: 420,
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: tokens.typography.fontSize.sm,
    color: tokens.semantic.textMuted,
    lineHeight: tokens.typography.lineHeight.normal,
  };

  return (
    <div ref={ref} style={containerStyles} role="status" aria-live="polite">
      <div aria-hidden style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {illustration ?? <DefaultIllustration />}
      </div>
      <h3 style={titleStyles}>{title}</h3>
      {description && <p style={descriptionStyles}>{description}</p>}
      {(primaryAction || secondaryAction) && (
        <div
          style={{
            display: 'flex',
            gap: tokens.spacing[3],
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginTop: tokens.spacing[2],
          }}
        >
          {primaryAction && renderAction(primaryAction, 'cta')}
          {secondaryAction && renderAction(secondaryAction, 'ghost')}
        </div>
      )}
    </div>
  );
});

export default EmptyState;
